import { defineStore } from 'pinia';
import { aoMudarConexao, assinar, estaConectado, ligar } from '@/services/tempoReal.js';
import {
    abrirConversaInterna,
    colegas,
    conversasInternas,
    enviarInterna,
    marcarInternaLida,
    mensagensInternas,
    naoLidasInternas,
} from '@/services/api/atendimento.js';

/**
 * O estado do chat interno.
 *
 * Mesma mecanica do atendimento -- polling por delta -- e pelos mesmos
 * motivos (ver `modules/atendimento/store.js`). Duas diferencas:
 *
 *  1. O contador de nao lidas roda mesmo com a tela fechada, porque ele
 *     alimenta o numerinho do menu: quem esta atendendo precisa ver que foi
 *     chamado sem ficar trocando de tela para conferir.
 *  2. Cada laco agenda o proximo ciclo no `finally`, com teto de espera. Foi a
 *     licao da caixa de entrada: requisicao pendurada matava o laco em
 *     silencio e a tela ficava parada com cara de viva.
 */
const RITMO_MENSAGENS = 3000;
const RITMO_LISTA = 8000;
const RITMO_CONTADOR = 20000;
const FATOR_ABA_ESCONDIDA = 4;

/** Com socket de pe o polling vira rede de seguranca -- ver o store do atendimento. */
const RITMO_COM_SOCKET = 30000;
const LIMITE_DO_CICLO = 20000;

let temporizadorMensagens = null;
let temporizadorLista = null;
let temporizadorContador = null;
let cancelarConversa = null;
let cancelarConexao = null;

const escondida = () => typeof document !== 'undefined' && document.hidden;

function ritmo(base) {
    const efetivo = estaConectado() ? Math.max(base, RITMO_COM_SOCKET) : base;

    return escondida() ? efetivo * FATOR_ABA_ESCONDIDA : efetivo;
}

function comLimite(promessa, limite = LIMITE_DO_CICLO) {
    return Promise.race([promessa, new Promise((resolve) => setTimeout(resolve, limite))]);
}

export const useInternoStore = defineStore('interno', {
    state: () => ({
        corp: null,
        conversas: [],
        colegas: [],
        naoLidas: 0,

        abertaId: null,
        mensagens: [],

        carregandoLista: false,
        enviando: false,
        erro: null,
    }),

    getters: {
        aberta: (state) => state.conversas.find((c) => c.id === state.abertaId) ?? null,
        ultimaMensagemId: (state) => state.mensagens.reduce((maior, m) => Math.max(maior, m.id), 0),
        setores: (state) => state.conversas.filter((c) => c.kind === 'team'),
        diretas: (state) => state.conversas.filter((c) => c.kind === 'direct'),
    },

    actions: {
        async carregarLista() {
            this.carregandoLista = true;

            try {
                const resposta = await conversasInternas();
                this.conversas = resposta.data ?? [];
                this.naoLidas = resposta.unread ?? 0;
                this.erro = null;
            } catch (erro) {
                this.erro = erro?.message ?? 'Nao foi possivel atualizar o chat interno.';
            } finally {
                this.carregandoLista = false;
            }
        },

        async carregarColegas() {
            try {
                this.colegas = (await colegas()).data ?? [];
            } catch (erro) {
                this.erro = erro?.message ?? 'Nao foi possivel carregar os colegas.';
            }
        },

        /** So o contador: e o que roda com a tela do chat interno fechada. */
        async atualizarContador() {
            try {
                this.naoLidas = (await naoLidasInternas()).unread ?? 0;
            } catch {
                // Contador do menu nao merece aviso na tela: o proximo ciclo
                // tenta de novo, e errar para menos aqui nao quebra nada.
            }
        },

        async abrir(id) {
            if (this.abertaId === id) {
                return;
            }

            cancelarConversa?.();
            cancelarConversa = null;

            this.abertaId = id;
            this.mensagens = [];

            if (this.corp && ligar()) {
                cancelarConversa = assinar(`atendimento.interno.${this.corp}.${id}`, () => {
                    this.buscarNovidades();
                });
            }

            const resposta = await mensagensInternas(id);
            this.mensagens = resposta.data ?? [];

            await this.marcarLida();
        },

        /** Abre pela pessoa ou pelo setor, criando a conversa se for a primeira. */
        async abrirCom({ agenteId = null, equipeId = null }) {
            const resposta = await abrirConversaInterna({ agenteId, equipeId });
            const conversa = resposta.data;

            if (!this.conversas.some((c) => c.id === conversa.id)) {
                this.conversas = [conversa, ...this.conversas];
            }

            await this.abrir(conversa.id);

            return conversa.id;
        },

        async buscarNovidades() {
            if (!this.abertaId) {
                return;
            }

            const resposta = await mensagensInternas(this.abertaId, this.ultimaMensagemId);
            const novas = resposta.data ?? [];

            if (novas.length === 0) {
                return;
            }

            // O envio otimista ja pode ter posto a mensagem na tela.
            const conhecidas = new Set(this.mensagens.map((m) => m.id));
            this.mensagens = [...this.mensagens, ...novas.filter((m) => !conhecidas.has(m.id))];

            await this.marcarLida();
        },

        async enviar(texto) {
            const conteudo = (texto ?? '').trim();

            if (!conteudo || !this.abertaId) {
                return;
            }

            this.enviando = true;

            try {
                const resposta = await enviarInterna(this.abertaId, conteudo);

                // Mesma corrida do atendimento: o sinal do websocket pode
                // trazer esta mensagem antes de o POST responder.
                const conhecidas = new Set(this.mensagens.map((m) => m.id));
                if (!conhecidas.has(resposta.data?.id)) {
                    this.mensagens = [...this.mensagens, resposta.data];
                }
                await this.carregarLista();
            } finally {
                this.enviando = false;
            }
        },

        async marcarLida() {
            if (!this.abertaId) {
                return;
            }

            await marcarInternaLida(this.abertaId);

            const conversa = this.conversas.find((c) => c.id === this.abertaId);

            if (conversa) {
                this.naoLidas = Math.max(0, this.naoLidas - (conversa.unread ?? 0));
                conversa.unread = 0;
            }
        },

        /** Os dois laços da tela aberta: conversa e lista. */
        ligarRelogios() {
            this.desligarRelogios();

            const cicloMensagens = async () => {
                try {
                    await comLimite(this.buscarNovidades());
                } catch {
                    // Falha de um ciclo nao derruba o laco: a lista mostra o
                    // aviso e o proximo ciclo tenta de novo.
                } finally {
                    temporizadorMensagens = setTimeout(cicloMensagens, ritmo(RITMO_MENSAGENS));
                }
            };

            const cicloLista = async () => {
                try {
                    await comLimite(this.carregarLista());
                } finally {
                    temporizadorLista = setTimeout(cicloLista, ritmo(RITMO_LISTA));
                }
            };

            cicloMensagens();
            cicloLista();
        },

        /**
         * O canal do proprio agente, que traz recado de qualquer conversa --
         * e o que faz o numerinho do menu subir sem a tela estar aberta.
         */
        ligarTempoReal(corp, agenteId) {
            this.corp = corp ?? this.corp;

            if (!this.corp || !agenteId || !ligar()) {
                return;
            }

            cancelarConexao?.();
            cancelarConexao = aoMudarConexao(() => {
                if (temporizadorLista) {
                    this.ligarRelogios();
                }
            });

            return assinar(`atendimento.agente.${this.corp}.${agenteId}`, () => {
                this.atualizarContador();

                if (temporizadorLista) {
                    this.carregarLista();
                }
            });
        },

        desligarRelogios() {
            [temporizadorMensagens, temporizadorLista].forEach(clearTimeout);
            temporizadorMensagens = null;
            temporizadorLista = null;
            cancelarConversa?.();
            cancelarConversa = null;
            cancelarConexao?.();
            cancelarConexao = null;
        },

        /**
         * O laco do numerinho do menu. Vive fora da tela do chat interno e nao
         * e desligado por ela -- so pela saida da aplicacao.
         */
        ligarContador() {
            if (temporizadorContador) {
                return;
            }

            const ciclo = async () => {
                try {
                    await comLimite(this.atualizarContador());
                } finally {
                    temporizadorContador = setTimeout(ciclo, ritmo(RITMO_CONTADOR));
                }
            };

            ciclo();
        },

        desligarContador() {
            clearTimeout(temporizadorContador);
            temporizadorContador = null;
        },
    },
});
